import { useMemo } from 'react';
import Select, { Value } from 'domains/core/components/Select';
import { Send } from 'domains/campaigns/types';
import {
    ChannelSegmentFilter,
    EngagementName,
    EngagementUIOperator,
    isChannelSegmentFilterArray,
    SegmentFilter,
    SegmentFilterEngagementFields,
    SegmentFilterState,
} from 'domains/segments/types';
import SendAutocomplete from './SendAutocomplete';
import ValueAutocomplete from './ValueAutocomplete';

const engagementOperatorToName: Record<string, EngagementName> = {
    // Common mappings (used by both Version 2 and Version 3) - all lowercase to match EngagementUIOperator enum
    'did receive': EngagementName.RECEIVED_SENDS,
    'did not receive': EngagementName.RECEIVED_SENDS,
    'did click': EngagementName.CLICKED_SENDS,
    'did not click': EngagementName.CLICKED_SENDS,
    'did open': EngagementName.OPENED_SENDS,
    'did not open': EngagementName.OPENED_SENDS,
    // Version 3 mappings - lowercase
    'did reply': EngagementName.REPLIED_SENDS,
    'did not reply': EngagementName.REPLIED_SENDS,
    'did deliver': EngagementName.DELIVERED_SENDS,
    'did not deliver': EngagementName.DELIVERED_SENDS,
    [EngagementUIOperator.CALL_ANSWERED]: EngagementName.CALL_ANSWERED_SENDS,
    [EngagementUIOperator.CALL_NOT_ANSWERED]: EngagementName.NOT_PICKED_SENDS,
    [EngagementUIOperator.VOICEMAIL]: EngagementName.VOICEMAIL_SENT_SENDS,
    [EngagementUIOperator.CALL_FAILED]: EngagementName.CALL_FAILED_SENDS,
    [EngagementUIOperator.CALL_TRANSFERRED]: EngagementName.CALL_TRANSFERED_SENDS,
    [EngagementUIOperator.SENT]: EngagementName.SENT_SENDS,
    [EngagementUIOperator.NOT_SENT]: EngagementName.SENT_SENDS,
    // Alternative mappings for delivered (in case they come through without `did ` prefix)
    // TODO: remove this once BE returns these operators like other operators (`did ` prefix)
    delivered: EngagementName.DELIVERED_SENDS,
    'not delivered': EngagementName.DELIVERED_SENDS,
};

export type ActivityFilterComponents = {
    channelSelect: React.ReactNode | null;
    operatorSelect: React.ReactNode | null;
    sendAutocomplete: React.ReactNode | null;
    valueAutocomplete: React.ReactNode | null;
};

export type Props = {
    filter: SegmentFilterState<SegmentFilterEngagementFields>;
    index: number;
    engagementFilters: SegmentFilter[] | ChannelSegmentFilter[];
    sends: Send[];
    updateSegmentFilter: (
        index: number,
        filter: SegmentFilterState<SegmentFilterEngagementFields>,
        resetPreview?: boolean
    ) => void;
};

const ActivityFilter = ({ filter, index, engagementFilters, sends, updateSegmentFilter }: Props) => {
    const shouldUseEnhancedFiltering = isChannelSegmentFilterArray(engagementFilters);

    const { operator, categorySpecificFields } = filter;
    const { channel } = categorySpecificFields;

    const channelOptions: Value[] = useMemo(() => {
        if (!engagementFilters?.length) return [];

        if (!shouldUseEnhancedFiltering) return [];

        return engagementFilters
            .slice() // Don't mutate original
            .sort((a, b) => a.channelDisplayName.localeCompare(b.channelDisplayName)) // Sort by ascending order
            .map((filter) => ({
                label: filter.channelDisplayName,
                value: filter.channel,
            }));
    }, [engagementFilters, shouldUseEnhancedFiltering]);

    const engagementOperators: Value[] = useMemo(() => {
        if (!engagementFilters?.length) return [];

        // Handle Version 3 (channel-based filters)
        if (shouldUseEnhancedFiltering) {
            // Filter by selected channel if enhancement is enabled and channel is selected
            const channelFiltersToUse = channel
                ? engagementFilters.filter((channelFilter) => channelFilter.channel === channel)
                : engagementFilters;

            const displayNames = channelFiltersToUse.flatMap((channelFilter) =>
                channelFilter.filters.map((filter) => filter.displayName)
            );

            // Convert display names to lowercase operators with special handling for delivered
            return displayNames.map((displayName) => {
                let operatorValue = displayName.toLowerCase();

                // Special handling for delivered operators to match EngagementUIOperator enum
                // TODO: remove this once BE returns these operators like other operators (`did ` prefix)
                if (operatorValue === 'delivered' || operatorValue === 'did deliver') {
                    operatorValue = 'did deliver';
                } else if (operatorValue === 'not delivered' || operatorValue === 'did not deliver') {
                    operatorValue = 'did not deliver';
                }

                return {
                    label: operatorValue,
                    value: operatorValue,
                };
            });
        }

        // Handle Version 2 (legacy filters) - Add "did" and "did not" prefixes
        const displayNames = engagementFilters.map((filter) => filter.displayName);

        return displayNames.flatMap((displayName) => [
            {
                label: `did ${displayName}`,
                value: `did ${displayName}`,
            },
            {
                label: `did not ${displayName}`,
                value: `did not ${displayName}`,
            },
        ]);
    }, [engagementFilters, shouldUseEnhancedFiltering, channel]);

    const activeChannelEngagementFilters: ChannelSegmentFilter['filters'] =
        shouldUseEnhancedFiltering &&
        engagementFilters.find(
            (engagementFilter: ChannelSegmentFilter) => engagementFilter.channel === categorySpecificFields.channel
        )?.filters;

    const activeChannelEngagementFilter =
        shouldUseEnhancedFiltering &&
        activeChannelEngagementFilters?.find((engagementFilter) => {
            if (filter.operator === EngagementUIOperator.DID_DELIVER) {
                return engagementFilter.displayName === 'Delivered';
            } else if (filter.operator === EngagementUIOperator.DID_NOT_DELIVER) {
                return engagementFilter.displayName === 'Not Delivered';
            } else {
                return engagementFilter.displayName.toLowerCase() === filter.operator;
            }
        });

    const shouldShowValuesAutocomplete =
        !!activeChannelEngagementFilter?.valuesRef ||
        engagementOperatorToName[operator] === EngagementName.CLICKED_SENDS;

    const handleChannelChange = (e: any) => {
        updateSegmentFilter(index, {
            ...filter,
            autocompleteInputValue: '',
            autocompleteValue: null,
            categorySpecificFields: {
                ...categorySpecificFields,
                channel: e.target.value,
                value: null,
                sendInteractionField: {
                    value: null,
                    autocompleteInputValue: '',
                    autocompleteValue: null,
                },
            },
            operator: null,
        });
    };

    const handleOperatorChange = (e: any) => {
        updateSegmentFilter(index, {
            ...filter,
            name: engagementOperatorToName[e.target.value],
            operator: e.target.value,
            categorySpecificFields: {
                ...categorySpecificFields,
                value: null,
                sendInteractionField: {
                    value: null,
                    autocompleteInputValue: '',
                    autocompleteValue: null,
                },
            },
            autocompleteInputValue: '',
            autocompleteValue: null,
        });
    };

    // Return individual components for flexible layout
    const components: ActivityFilterComponents = {
        channelSelect: shouldUseEnhancedFiltering ? (
            <Select
                label="Channel"
                sx={{ textTransform: 'capitalize', width: '12rem' }}
                value={channel ?? ''}
                values={channelOptions}
                onChange={handleChannelChange}
            />
        ) : null,
        operatorSelect:
            !shouldUseEnhancedFiltering || channel ? (
                <Select
                    label="Operator"
                    data-test="activity-operator-select"
                    data-testid="activity-operator-select"
                    sx={{ textTransform: 'capitalize', width: '12rem' }}
                    value={operator ?? ''}
                    values={engagementOperators}
                    onChange={handleOperatorChange}
                />
            ) : null,
        sendAutocomplete: operator ? (
            <SendAutocomplete
                activeChannelEngagementFilter={activeChannelEngagementFilter}
                engagementFilters={engagementFilters}
                filter={filter}
                index={index}
                sends={sends}
                updateSegmentFilter={updateSegmentFilter}
            />
        ) : null,
        valueAutocomplete: shouldShowValuesAutocomplete ? (
            <ValueAutocomplete
                activeChannelEngagementFilter={activeChannelEngagementFilter}
                filter={filter}
                index={index}
                updateSegmentFilter={updateSegmentFilter}
            />
        ) : null,
    };

    return components;
};

export default ActivityFilter;
